import React, { useState, useEffect } from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import DoctorFooter from "./DoctorFooter";
import { Button, InputBase, IconButton } from "@mui/material";
import { CreditCard, Menu, PiggyBank, UserRound } from "lucide-react";
import axios from "axios";

const inputSx = {
  px: 2,
  py: 1.2,
  fontSize: "14px",
  color: "#0f172a",
  "& input::placeholder": {
    color: "#94a3b8",
    opacity: 1,
  },
};

const Field = ({ label, children }) => {
  return (
    <div>
      <label className="mb-2 block text-xs font-medium text-slate-500">
        {label}
      </label>

      <div className="rounded-xl border border-slate-200 bg-white transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-100">
        {children}
      </div>
    </div>
  );
};

const DoctorSettingsPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [doctorData, setDoctorData] = useState({
    id: "",
    name: "",
    email: "",
    address: "",
    phoneNumber: "",
    age: "",
    sex: "",
    mdcnNumber: "",
    Nin: "",
    bankName: "",
    accountNumber: "",
  });

  useEffect(() => {
    const storedDoctor = JSON.parse(localStorage.getItem("doctor") || "{}");

    if (storedDoctor) {
      setDoctorData({
        id: storedDoctor._id || "",
        name: storedDoctor.name || "",
        email: storedDoctor.email || "",
        address: storedDoctor.address || "",
        phoneNumber: storedDoctor.phoneNumber || "",
        age: storedDoctor.age || "",
        sex: storedDoctor.sex || "",
        mdcnNumber: storedDoctor.mdcnNumber || "",
        Nin: storedDoctor.Nin || "",
        bankName: storedDoctor.bankName || "",
        accountNumber: storedDoctor.accountNumber || "",
      });
    }
  }, []);

  const handleChange = (field, value) => {
    setDoctorData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const payload = {
        id: doctorData.id,
        name: doctorData.name,
        email: doctorData.email,
        address: doctorData.address,
        phoneNumber: doctorData.phoneNumber,
        age: doctorData.age,
        sex: doctorData.sex,
        bankName: doctorData.bankName,
        accountNumber: doctorData.accountNumber,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/doctor/update`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      localStorage.setItem("doctor", JSON.stringify(res.data.doctor));

      alert("Profile updated successfully");
    } catch (error) {
      console.error(error);
      alert("Error updating profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Dashboard</p>
          <h1 className="text-base font-semibold text-slate-950">Settings</h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Settings" setActiveTab={() => {}} />
        </div>

        {/* Mobile drawer */}
        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Settings"
          setActiveTab={() => setDrawerOpen(false)}
        />

        {/* Main */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page title */}
            <div className="mb-8">
              <h1 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                Settings
              </h1>

              <p className="mt-2 text-sm text-slate-500">
                Manage your profile and payment details.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_0.7fr]">
              {/* Profile */}
              <section className="rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-100 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                      <UserRound size={18} />
                    </div>

                    <div>
                      <h2 className="text-base font-semibold text-slate-950">
                        Profile information
                      </h2>
                      <p className="mt-1 text-sm text-slate-500">
                        Basic doctor account details.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2">
                  <Field label="Full name">
                    <InputBase
                      placeholder="Full name"
                      value={doctorData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      fullWidth
                      sx={inputSx}
                    />
                  </Field>

                  <Field label="Email">
                    <InputBase
                      placeholder="Email"
                      type="email"
                      value={doctorData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      fullWidth
                      sx={inputSx}
                    />
                  </Field>

                  <Field label="Phone number">
                    <InputBase
                      placeholder="Phone number"
                      value={doctorData.phoneNumber}
                      onChange={(e) =>
                        handleChange("phoneNumber", e.target.value)
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  </Field>

                  <Field label="Age">
                    <InputBase
                      placeholder="Age"
                      type="number"
                      value={doctorData.age}
                      onChange={(e) => handleChange("age", e.target.value)}
                      fullWidth
                      sx={inputSx}
                    />
                  </Field>

                  <Field label="Sex">
                    <select
                      value={doctorData.sex}
                      onChange={(e) => handleChange("sex", e.target.value)}
                      className="h-[48px] w-full rounded-xl bg-transparent px-4 text-sm text-slate-800 outline-none"
                    >
                      <option value="">Select sex</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </Field>

                  <Field label="Address">
                    <InputBase
                      placeholder="Address"
                      value={doctorData.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      fullWidth
                      sx={inputSx}
                    />
                  </Field>

                  <Field label="MDCN number">
                    <InputBase
                      placeholder="MDCN number"
                      value={doctorData.mdcnNumber}
                      disabled
                      fullWidth
                      sx={{
                        ...inputSx,
                        color: "#64748b",
                      }}
                      className="bg-slate-50"
                    />
                  </Field>

                  <Field label="NIN number">
                    <InputBase
                      placeholder="NIN number"
                      value={doctorData.Nin}
                      disabled
                      fullWidth
                      sx={{
                        ...inputSx,
                        color: "#64748b",
                      }}
                      className="bg-slate-50"
                    />
                  </Field>
                </div>
              </section>

              {/* Payment */}
              <section className="h-fit rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h2 className="text-base font-semibold text-slate-950">
                    Payment information
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Bank details for future payouts.
                  </p>
                </div>

                <div className="space-y-5 p-5">
                  <div>
                    <label className="mb-2 block text-xs font-medium text-slate-500">
                      Bank name
                    </label>

                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-100">
                      <PiggyBank size={17} className="text-slate-400" />

                      <InputBase
                        placeholder="Bank name"
                        value={doctorData.bankName}
                        onChange={(e) =>
                          handleChange("bankName", e.target.value)
                        }
                        fullWidth
                        sx={inputSx}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-medium text-slate-500">
                      Account number
                    </label>

                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-100">
                      <CreditCard size={17} className="text-slate-400" />

                      <InputBase
                        placeholder="Account number"
                        value={doctorData.accountNumber}
                        onChange={(e) =>
                          handleChange("accountNumber", e.target.value)
                        }
                        fullWidth
                        sx={inputSx}
                      />
                    </div>
                  </div>

                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-sm font-medium text-slate-700">
                      Payout note
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      Earnings and withdrawals will use these details when the
                      payout feature is enabled.
                    </p>
                  </div>
                </div>
              </section>
            </div>

            {/* Save */}
            <div className="mt-8 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={loading}
                sx={{
                  bgcolor: "#075E54",
                  color: "white",
                  borderRadius: "12px",
                  px: 4,
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#064b43",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    bgcolor: "#e2e8f0",
                    color: "#64748b",
                  },
                }}
              >
                {loading ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </main>
      </div>

    </div>
  );
};

export default DoctorSettingsPage;
